import { JobStatus, Report } from '@/api/generatedApi';
import { Box, Button, CircularProgress, styled, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useParams } from 'react-router-dom';
import { useGetReport } from '@/hooks/api/reports/useGetReport';
import { ReportDetailsCell } from './ReportDetailsCell';
import { useInfiniteReportGroups } from '@/hooks/api/reports/useInfiniteReportGroups';
import { ApiErrorAlert } from '../Errors/ApiErrorAlert';
import { getApiError } from '@/types/apiError';
import { pluralize } from '@/utils/format';
import { BodyText } from '../BodyText';
import { ReportGroupsList } from './TableView/ReportGroupsList';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useRef, useState } from 'react';
import { ReportPageObjectModel } from '@/poms/reportPageObjectModel';
import { CreateReportGroupModal } from '../modals/CreateReportGroupModal';
import AddIcon from '@mui/icons-material/Add';

const StyledCard = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '6px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  padding: '0 12px 12px 12px',
  overflowX: 'hidden',
  overflowY: 'auto',
  flexShrink: 0,
  height: '100%',
  minHeight: 0,
}));

const GroupsListContainer = styled(Box)({
  flex: 1,
  minHeight: 0,
  marginTop: '4px',
  position: 'relative',
  paddingTop: '4px',
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

const CreateGroupButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const ReportContent = ({ report, totalGroupsCount }: { report: Report; totalGroupsCount: number }) => {
  return (
    <Box display='flex' flexDirection='column'>
      <Box>
        <SectionTitle>Details</SectionTitle>
        <ReportDetailsCell report={report} />
      </Box>
      <Box>
        <SectionTitle>Created</SectionTitle>
        <Box display='flex' flexDirection='row' gap={1} alignItems='center'>
          <Typography variant='body2' color='text.secondary'>
            {formatDistanceToNow(new Date(report.createdAt), { addSuffix: true })}
          </Typography>
          {report.status === JobStatus.RUNNING && (
            <>
              <CircularProgress sx={{ color: 'text.secondary' }} size={14} />
              <Typography variant='body2' color='text.secondary'>
                {report.progress}%
              </Typography>
            </>
          )}
          {report.status === JobStatus.FAILED && (
            <Typography variant='body2' color='error'>
              Failed
            </Typography>
          )}
        </Box>
      </Box>
      {report.creator && (
        <Box>
          <SectionTitle>Created By</SectionTitle>
          <Typography variant='body2'>{report.creator.email}</Typography>
        </Box>
      )}
      <Box>
        <SectionTitle>Groups</SectionTitle>
        <BodyText color='text.secondary'>{pluralize(totalGroupsCount, 'Group', 'Groups')}</BodyText>
      </Box>
    </Box>
  );
};

export const ReportInfo = ({ isDragging }: { isDragging?: boolean }) => {
  const { id, groupId } = useParams();
  const { report, isLoading, error } = useGetReport({ id });
  const {
    groups,
    isLoading: isLoadingGroups,
    error: errorGroups,
    total: totalGroupsCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteReportGroups({ id });
  const listReference = useRef<HTMLDivElement>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useInfiniteScroll(listReference, {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  if (isLoading || isLoadingGroups || !report) {
    return <CircularProgress size={20} />;
  }

  if (error || errorGroups) {
    return <ApiErrorAlert error={getApiError(error || errorGroups, 'Failed to load report')} />;
  }

  const hasGroupsWithAssets = groups.some(group => group.assetIds.length > 0);

  return (
    <StyledCard data-testid={ReportPageObjectModel.ReportInfo}>
      <ReportContent report={report} totalGroupsCount={totalGroupsCount} />
      <GroupsListContainer ref={listReference}>
        <ReportGroupsList groups={groups} selectedGroupId={groupId} isDragging={isDragging} />
      </GroupsListContainer>
      {hasGroupsWithAssets && (
        <CreateGroupButtonContainer>
          <Button variant='outlined' startIcon={<AddIcon />} onClick={() => setIsCreateModalOpen(true)} size='small'>
            Create Group
          </Button>
        </CreateGroupButtonContainer>
      )}
      {id && (
        <CreateReportGroupModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} reportId={id} />
      )}
    </StyledCard>
  );
};
